import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PersonaLabel {
  key: string;
  label: string;
  icon: string;
}

// Hardcoded fallback used when the DB row is missing.
const FALLBACK: PersonaLabel[] = [
  { key: "it-telecoms", label: "IT / Telecoms Leader", icon: "" },
  { key: "finance", label: "Finance / Procurement", icon: "" },
  { key: "partner", label: "Channel Partner", icon: "" },
  { key: "operations", label: "Operations / Frontline", icon: "" },
  { key: "uc-admin", label: "UC / Platform Admin", icon: "" },
];

function parseRows(raw: unknown): PersonaLabel[] {
  let val: unknown = raw;
  if (typeof val === "string") {
    try { val = JSON.parse(val); } catch { return FALLBACK; }
  }
  if (!Array.isArray(val)) return FALLBACK;
  const cleaned = val
    .filter((r: any) => r && typeof r.key === "string" && typeof r.label === "string")
    .map((r: any) => ({ key: r.key, label: r.label, icon: typeof r.icon === "string" ? r.icon : "" }));
  return cleaned.length ? cleaned : FALLBACK;
}

/**
 * Source-of-truth hook for visitor role tiles.
 * Reads tanya_config.persona_options (managed in admin: Tanya → Personalisation → Visitor Roles)
 * and returns an ordered list plus a lookup map. Falls back to a hardcoded set.
 */
export function usePersonaLabels() {
  const { data } = useQuery({
    queryKey: ["persona-labels"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tanya_config" as any)
        .select("value")
        .eq("key", "persona_options")
        .maybeSingle();
      if (error) return FALLBACK;
      return parseRows((data as any)?.value);
    },
  });

  const list = data ?? FALLBACK;
  const map: Record<string, { label: string; icon: string }> = {};
  for (const r of list) map[r.key] = { label: r.label, icon: r.icon };
  return { list, map };
}

export const PERSONA_LABELS_FALLBACK = FALLBACK;